import { type UseMutationResult } from '@tanstack/react-query'
import { ArrowRightIcon } from 'lucide-react'
import { useEffect, useState } from 'react'
import { useForm } from 'react-hook-form'

import { type GrapeAPIErrorType } from 'components/hooks/GrapeAPIError'
import { FormNumberFieldDS, FormRadioGroupDS, FormTelephoneInputDS } from 'components/reusable/form/FormFields'
import { SlabAddressFields } from 'components/reusable/form/SlabAddressFields'
import { type StateFormData as GetStateFormData } from 'hooks/useGetStateFormData'
import { type StateFormData } from 'hooks/useStateFormMutation'
import { Banner, Button, ButtonGroup, InputBlock, TextButton, useBreakpoint } from 'slab'

import { useOnboarding } from './member_onboarding_v2/OnboardingContext'
import LoadingSpinnerCentered from './reusable/LoadingSpinnerCentered'

type NewJerseyConfigForm = {
  has_other_residence: string | boolean;
  other_residence_duration_in_months: number;
  address_line1: string;
  address_line2: string;
  city: string;
  state: string;
  zip: string;
  country: string;
  phone_number: string;
}

const NewJerseyForm = ({
  navigateToNextStep,
  navigateToPreviousStep,
  stateFormMutation,
  stateFormData,
  isLoading,
  error,
  refetch,
}: {
  navigateToNextStep?: () => void;
  navigateToPreviousStep?: () => void;
  stateFormMutation: UseMutationResult<void, GrapeAPIErrorType, StateFormData, unknown>;
  stateFormData: GetStateFormData | undefined;
  isLoading: boolean;
  error: GrapeAPIErrorType | null;
  refetch: () => void;
}) => {
  const { mapboxAccessToken, onboardingData } = useOnboarding()

  const { breakpointIsAtLeast } = useBreakpoint()

  const getDefaultValues = () => ({
    has_other_residence: stateFormData?.state_config?.has_other_residence ? 'true' : 'false',
    other_residence_duration_in_months: stateFormData?.state_config?.other_residence_duration_in_months || 1,
    address_line1: stateFormData?.state_config?.other_residence_address_line1 || '',
    other_residence_address_line2: stateFormData?.state_config?.other_residence_address_line2 || '',
    city: stateFormData?.state_config?.other_residence_city || '',
    state: stateFormData?.state_config?.other_residence_state || '',
    zip: stateFormData?.state_config?.other_residence_zip || '',
    country: stateFormData?.state_config?.other_residence_country || '',
    phone_number: stateFormData?.state_config?.other_residence_phone || onboardingData?.member?.phone_number || '',
  })

  const {
    register,
    control,
    handleSubmit,
    watch,
    getValues,
    setValue,
    trigger,
    setError,
    clearErrors,
    reset,
    formState: { errors, isValid },
  } = useForm<NewJerseyConfigForm>({
    defaultValues: getDefaultValues(),
    mode: 'onChange',
  })

  const [submitError, setSubmitError] = useState<string | null>(null)
  const existingNonAmericanAddress = stateFormData?.state_config?.other_residence_country && stateFormData?.state_config?.other_residence_country !== 'United States'
  const [hasNonAmericanAddress, setHasNonAmericanAddress] = useState(existingNonAmericanAddress)

  const hasOtherResidenceInput = watch('has_other_residence')
  const hasOtherResidence = typeof hasOtherResidenceInput === 'string' ? hasOtherResidenceInput === 'true' : hasOtherResidenceInput

  const getPayload = (formData: NewJerseyConfigForm, otherResidenceCountry: string) => {
    if (!formData.has_other_residence || formData.has_other_residence === 'false') {
      return {
        has_other_residence: false
      }
    }

    return {
      has_other_residence: true,
      other_residence_duration_in_months: formData.other_residence_duration_in_months,
      other_residence_address_line1: formData.address_line1,
      other_residence_address_line2: formData.address_line2,
      other_residence_city: formData.city,
      other_residence_state: formData.state,
      other_residence_zip: formData.zip,
      other_residence_country: otherResidenceCountry,
      other_residence_phone: formData.phone_number,
    }
  }

  const onFormSubmit = async (formData: NewJerseyConfigForm) => {
    const otherResidenceCountry = hasNonAmericanAddress ? formData.country : 'United States'

    try {
      if (formData.has_other_residence === 'true') {
        if (!formData.other_residence_duration_in_months ||
            formData.other_residence_duration_in_months < 1 ||
            formData.other_residence_duration_in_months > 11) {
          setSubmitError('Duration must be between 1 and 11 months')
          return
        }

        // Not all countries have states
        // However, our backend assumes that the state exists so this is a required field
        if (!formData.address_line1 ||
            !formData.city ||
            !formData.state ||
            !formData.zip ||
            !otherResidenceCountry ||
            !formData.phone_number) {
          setSubmitError('Please fill in all required fields')
          return
        }
      }

      const payload = getPayload(formData, otherResidenceCountry)

      await stateFormMutation.mutateAsync(payload, {
        onSuccess: () => {
          refetch()
          navigateToNextStep?.()
        },
      })
    } catch (error) {
      setSubmitError(error instanceof Error ? error.message : 'An error occurred while submitting the form')
    }
  }

  // state form data can be slow to load, so want to make sure we update the defaults when it's ready
  useEffect(() => {
    reset(getDefaultValues())
  }, [stateFormData, onboardingData])

  if (isLoading) {
    return <div><LoadingSpinnerCentered /></div>
  }

  if (error) {
    return (
      <div className='mb-4 p-4 bg-red-50 border border-red-200 rounded-md'>
        <Banner
          title='Something went wrong'
          type='critical'
        >
          <p>We couldn't load this page's data. Please try again later.</p>
        </Banner>
      </div>
    )
  }

  function renderButtons () {
    const backButton = (
      <Button
        variant='secondary'
        onClick={() => {
          navigateToPreviousStep?.()
        }}
      >
        Back
      </Button>
    )

    const nextButton = (
      <Button
        rightIcon={ArrowRightIcon}
        variant='primary'
        type='submit'
        disabled={stateFormMutation.isPending || !isValid}
      >
        Next
      </Button>
    )

    if (breakpointIsAtLeast('xs')) {
      return (
        <ButtonGroup size='large'>
          {backButton}
          {nextButton}
        </ButtonGroup>
      )
    }

    return (
      <ButtonGroup size='large' layout='vertical'>
        {nextButton}
        {backButton}
      </ButtonGroup>
    )
  }

  return (
    <>
      <div className='mb-8'>
        <p className='mb-3 body text-gray-500'>Applying for healthcare coverage in New Jersey requires that we ask you some additional questions before enrolling you in a plan. We will only use this information to submit the plan application to your insurance company.</p>
      </div>
      <form onSubmit={handleSubmit(onFormSubmit)} className='flex flex-col gap-y-6'>
        <FormRadioGroupDS
          control={control}
          name='has_other_residence'
          buttonVariant='bordered'
          layout='horizontal'
          buttonSize='small'
          label='Do you maintain a home in any other state or country?'
          options={[
            {
              label: 'Yes',
              value: 'true',
            },
            { label: 'No', value: 'false' },
          ]}
        />
        {hasOtherResidence && (
          <div>
            <div className='flex flex-col gap-y-6'>
              <div className='max-w-96'>
                <FormNumberFieldDS
                  fieldId='other_residence_duration_in_months'
                  fieldLabel='How many months do you live at your other residence each year?'
                  register={register}
                  customWidth='max-w-96'
                  error={errors.other_residence_duration_in_months}
                  placeholder='1'
                  validationRule={{
                    min: {
                      value: 1,
                      message: 'Duration must be between 1 and 11 months',
                    },
                    max: {
                      value: 11,
                      message: 'Duration must be between 1 and 11 months',
                    },
                  }}
                />
              </div>

              {!hasNonAmericanAddress && (
                <div className='flex flex-col gap-y-2'>
                  <SlabAddressFields<NewJerseyConfigForm>
                    accessToken={mapboxAccessToken!}
                    errors={errors}
                    disabled={stateFormMutation.isPending}
                    getValues={getValues}
                    setValue={setValue}
                    register={register}
                    trigger={trigger}
                    setError={setError}
                    clearErrors={clearErrors}
                    watch={watch}
                  />
                  <div className='body-sm text-gray-500'>
                    <TextButton
                      onClick={() => setHasNonAmericanAddress(true)}
                    >
                      My other residence is outside the United States
                    </TextButton>
                  </div>
                </div>
              )}
              {
                hasNonAmericanAddress && (
                  <InputBlock label='Address'>
                    <InputBlock.Row>
                      <InputBlock.Input
                        label='Street address'
                        {...register('address_line1')}
                      />
                    </InputBlock.Row>
                    <InputBlock.Row>
                      <InputBlock.Input
                        label='Address line 2'
                        {...register('address_line2')}
                      />
                    </InputBlock.Row>
                    <InputBlock.Row>
                      <InputBlock.Input
                        label='City'
                        span='3'
                        {...register('city')}
                      />
                      <InputBlock.Input
                        label='State'
                        span='2'
                        {...register('state')}
                      />
                    </InputBlock.Row>
                    <InputBlock.Row>
                      <InputBlock.Input
                        label='Zip/Postal Code'
                        span='2'
                        maxLength={10}
                        {...register('zip')}
                      />
                      <InputBlock.Input
                        label='Country'
                        span='3'
                        {...register('country')}
                      />
                    </InputBlock.Row>
                  </InputBlock>
                )
              }
              <FormTelephoneInputDS<NewJerseyConfigForm>
                customWidth='max-w-40'
                error={errors.phone_number}
                disabled={stateFormMutation.isPending}
                fieldId='phone_number'
                fieldLabel='Phone number'
                register={register}
                placeholder='(123) 456-7890'
                validationRule={{ required: 'Phone number is required.' }}
              />
            </div>
          </div>
        )}

        {submitError && (
          <div className='mb-4 p-4 bg-red-50 border border-red-200 rounded-md'>
            <p className='body text-red-700'>{submitError}</p>
          </div>
        )}

        <div className='mt-10'>
          {renderButtons()}
        </div>
      </form>
    </>
  )
}

export default NewJerseyForm
